import { LIMITS, type RulesConfig, type Seat, normalizeRoomCode, normalizeRules } from '@euchre/core';
import { feedbackButton } from './extras.js';
import { h } from '../dom.js';
import { storeName, storeRules, storedRules } from '../net.js';
import { IS_PREVIEW, type TableMode, app, rerender, send, toast } from '../state.js';
import { rulesEditor, rulesSummary } from './rules.js';
import { startTutorial } from './tutorial.js';
import { accountPanel } from './account.js';

let root: HTMLElement | null = null;
let statusEl: HTMLElement;
let buttons: HTMLButtonElement[] = [];
let rules: RulesConfig = storedRules();
let rulesBox: HTMLElement;
let rulesLine: HTMLElement;
let rulesAside: HTMLElement;
let nameInput: HTMLInputElement;
/** The saved name the box last showed. */
let shownName = app.name;

function commitName(input: HTMLInputElement): string | null {
  const name = input.value.trim();
  if (!name) {
    toast('Pick a name so the table knows who you are.');
    input.focus();
    return null;
  }
  if (name !== app.name) {
    app.name = name;
    storeName(name);
    send({ type: 'set_name', name });
  }
  return name;
}

/**
 * Start a table the way the host asked for. The host is seated straight away.
 * "Just me and bots" also fills the other three chairs and deals, so there's no lobby at all.
 * (The server handles messages from one connection in order, so these can be sent back to back.)
 */
function startTable(mode: TableMode): void {
  if (!commitName(nameInput)) return;
  app.ui.tableMode = mode;
  app.ui.lobbyRulesOpen = false;
  send({ type: 'create_room', rules: { ...rules } });
  send({ type: 'sit', seat: 0 });
  if (mode === 'bots') {
    for (const seat of [1, 2, 3] as Seat[]) send({ type: 'add_bot', seat });
    send({ type: 'set_ready', ready: true });
    send({ type: 'start_game' });
  }
}

function modeButton(mode: TableMode, title: string, detail: string, primary = false): HTMLButtonElement {
  return h(
    'button',
    { class: `btn mode-btn${primary ? ' primary' : ''}`, type: 'button', dataset: { mode }, onclick: () => startTable(mode) },
    h('span', { class: 'mode-title', text: title }),
    h('span', { class: 'mode-detail', text: detail }),
  );
}

export function homeScreen(): HTMLElement {
  if (root) {
    update();
    return root;
  }
  nameInput = h('input', {
    id: 'name',
    class: 'field',
    value: app.name,
    maxLength: LIMITS.nameMax,
    placeholder: 'e.g. Aunt Marge',
    autocomplete: 'nickname',
  });
  const codeInput = h('input', {
    id: 'code',
    class: 'field code-field',
    maxLength: 5,
    value: app.pendingJoin ?? '',
    placeholder: 'ABCDE',
    autocomplete: 'off',
    oninput: () => {
      codeInput.value = normalizeRoomCode(codeInput.value);
    },
  });

  const modes = [
    modeButton('bots', 'Just me and bots', 'Start playing right away against the computer.', true),
    modeButton('friends-bots', 'Friends and bots', 'Invite friends. Bots fill any empty chairs.'),
    modeButton('friends', 'Friends only', 'Wait for three friends to join.'),
  ];
  const join = h('button', {
    class: 'btn',
    type: 'submit',
    text: 'Join table',
  });
  buttons = [...modes, join];

  const joinForm = h(
    'form',
    {
      class: 'join-form',
      onsubmit: (ev) => {
        ev.preventDefault();
        const code = normalizeRoomCode(codeInput.value);
        if (code.length !== 5) {
          toast('Table codes are 5 letters and numbers — ask the host for theirs.');
          codeInput.focus();
          return;
        }
        if (commitName(nameInput)) send({ type: 'join_room', code });
      },
    },
    h('label', { htmlFor: 'code', text: 'Have a table code from a friend?' }),
    h('div', { class: 'row' }, codeInput, join),
  );

  statusEl = h('p', { class: 'conn-note' });
  rulesLine = h('p', { class: 'rules-line' });
  rulesBox = h('div');
  rulesAside = h(
    'aside',
    { class: 'house-rules', id: 'home-rules' },
    h('h2', { text: 'House rules' }),
    h('p', { class: 'hint', text: 'For tables you start. We remember your choices, and you can still change them before the first deal.' }),
    rulesBox,
    h('button', {
      class: 'btn small',
      type: 'button',
      text: 'Done',
      onclick: () => {
        app.ui.rulesOpen = false;
        rerender();
      },
    }),
  );

  root = h(
    'main',
    { class: 'home' },
    h(
      'section',
      { class: 'home-card' },
      h('h1', { class: 'wordmark' }, h('span', { text: "Dave's Euchre" })),
      IS_PREVIEW
        ? h(
            'p',
            { class: 'preview-note' },
            h('strong', { text: 'Preview version. ' }),
            'A new look we’re testing. Games here are separate from the regular game. ',
            feedbackButton('link-btn'),
          )
        : null,
      h('label', { htmlFor: 'name', text: 'Your name at the table' }),
      nameInput,
      accountPanel(),
      h('h2', { class: 'mode-heading', text: 'Who do you want to play with?' }),
      h('div', { class: 'modes' }, modes),
      rulesLine,
      // The tutorial runs in this browser, so it works even before the connection to the server is up.
      h(
        'button',
        { class: 'btn learn-btn', type: 'button', onclick: () => startTutorial() },
        h('span', { class: 'mode-title', text: 'Learn How to Play Euchre' }),
        h('span', { class: 'mode-detail', text: 'New to the game? A quick, hands-on lesson at a practice table.' }),
      ),
      h('div', { class: 'divider', attrs: { role: 'separator' } }),
      joinForm,
      statusEl,
      h('p', { class: 'home-foot' }, h('a', { attrs: { href: '/privacy', target: '_blank', rel: 'noopener' }, text: 'Privacy' })),
    ),
    rulesAside,
  );
  renderRules();
  update();
  return root;
}

function renderRules(): void {
  rulesBox.replaceChildren(
    rulesEditor(rules, {
      editable: true,
      showReset: true,
      onChange: (patch) => {
        rules = normalizeRules({ ...rules, ...patch });
        storeRules(rules);
        renderRules();
        update();
      },
    }),
  );
}

/** Called when the host changes rules in the lobby, so the home screen matches. */
export function rememberRules(next: RulesConfig): void {
  rules = normalizeRules(next);
  storeRules(rules);
  if (rulesBox) {
    renderRules();
    update();
  }
}

function update(): void {
  const ready = app.conn === 'open';
  accountPanel();
  // The saved name can change from elsewhere (signing in brings the account's name). Only then update the box,
  // so whatever someone has typed but not used yet stays put.
  if (app.name !== shownName) {
    shownName = app.name;
    if (document.activeElement !== nameInput) nameInput.value = app.name;
  }
  for (const b of buttons) b.disabled = !ready;
  statusEl.textContent = ready
    ? ''
    : app.conn === 'connecting'
      ? 'Connecting to the table server…'
      : app.conn === 'superseded'
        ? 'Paused: the game is open in another tab or device.'
        : 'Lost connection — trying again…';
  const open = app.ui.rulesOpen;
  rulesAside.hidden = !open;
  root?.classList.toggle('rules-open', open);
  rulesLine.replaceChildren(
    h('span', { class: 'rules-label', text: 'Rules: ' }),
    h('span', { text: rulesSummary(rules) }),
    ' ',
    h('button', {
      class: 'link-btn',
      type: 'button',
      text: open ? 'Hide' : 'Change',
      attrs: { 'aria-expanded': String(open), 'aria-controls': 'home-rules' },
      onclick: () => {
        app.ui.rulesOpen = !app.ui.rulesOpen;
        rerender();
      },
    }),
  );
}
